import React, { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import AcademyLogo from '../assets/hayaachessacademy-siteicon-favicon.png';
import { PIECE_SETS, BOARD_THEMES } from './Board/BoardConstants';
import IconDashboard from '../assets/3d_icons/icon_dashboard.png';
import IconArcade from '../assets/3d_icons/icon_arcade.png';
import IconArena from '../assets/3d_icons/icon_arena.png';
import IconClassroom from '../assets/3d_icons/icon_classroom.png';
import IconHomework from '../assets/3d_icons/icon_homework.png';
import IconTools from '../assets/3d_icons/icon_tools.png';
import IconLibrary from '../assets/3d_icons/icon_library.png';
import { CartoonAvatar } from './GamificationAssets';

export default function Layout({ user, role, activeTab, setActiveTab, joinedLiveClass, isClassroomLive, children }) {
    const [showCustomizer, setShowCustomizer] = useState(false);
    const [boardTheme, setBoardTheme] = useState('green');
    const [pieceTheme, setPieceTheme] = useState('cburnett');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
    const [isTablet, setIsTablet] = useState(window.innerWidth <= 1100);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [avatar, setAvatar] = useState('');
    const [userName, setUserName] = useState('');

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 900);
            setIsTablet(window.innerWidth <= 1100);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!user?.email) return;
        const unsub = onSnapshot(doc(db, "users", user.email.toLowerCase()), (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                if (data.boardTheme) setBoardTheme(data.boardTheme);
                if (data.pieceTheme) setPieceTheme(data.pieceTheme);
                if (data.avatar) setAvatar(data.avatar);
                if (data.name) setUserName(data.name);
            }
        });
        return () => unsub();
    }, [user]);

    // CartoonAvatar handles rendering — keeps renderUserAvatar for role-based fallback name

    const handleSelectTheme = async (newBoard, newPiece) => {
        if (!user?.email) return;
        try {
            await updateDoc(doc(db, "users", user.email.toLowerCase()), {
                boardTheme: newBoard,
                pieceTheme: newPiece
            });
        } catch(e) {
            console.error("Theme save error:", e);
        }
    };

    const handleLogout = () => {
        signOut(auth);
    };

    // Sidebar Menu Items
    const menuItems = [
        { id: 'dashboard', label: '📊 Dashboard' },
        { id: 'chess-worlds', label: '🎮 Puzzle Arcade' },
        { id: 'arena', label: '⚔️ Arena' },
        { id: 'classroom', label: '🏫 Classroom' },
        { id: 'homework', label: '📝 Homework' },
        { id: 'board-tools', label: '🛠️ Board Tools' },
        ...(role !== 'STUDENT' ? [{ id: 'library', label: '📚 Library' }] : [])
    ];

    const pieceThemes2D = [
        { id: 'cburnett', name: 'Classic' },
        { id: 'merida', name: 'Merida' },
        { id: 'alpha', name: 'Alpha' },
        { id: 'maestro', name: 'Maestro' },
        { id: 'staunty', name: 'Staunty' },
        { id: 'caliente', name: 'Caliente' },
        { id: 'california', name: 'California' },
        { id: 'cardinal', name: 'Cardinal' },
        { id: 'dubrovny', name: 'Dubrovny' },
        { id: 'fantasy', name: 'Fantasy' },
        { id: 'fresca', name: 'Fresca' },
        { id: 'governor', name: 'Governor' },
        { id: 'horsey', name: 'Horsey 🐴' },
        { id: 'kiwen-suwi', name: 'Kiwen Suwi' },
        { id: 'kosal', name: 'Kosal' },
        { id: 'leipzig', name: 'Leipzig' },
        { id: 'letter', name: 'Letter' },
        { id: 'pixel', name: 'Pixel' },
        { id: 'riohacha', name: 'Riohacha' },
        { id: 'shapes', name: 'Shapes' },
        { id: 'spatial', name: 'Spatial' },
        { id: 'tatiana', name: 'Tatiana' },
        { id: 'pirouetti', name: 'Pirouetti' },
        { id: 'chess7', name: 'Chess7' },
        { id: 'companion', name: 'Companion' },
        { id: 'celtic', name: 'Celtic' }
    ];

    const pieceThemes3D = [
        { id: 'staunton3d', name: '3D Staunton' },
        { id: 'glass3d', name: '3D Glass' },
        { id: 'wood3d', name: '3D Wood' },
        { id: 'metal3d', name: '3D Metal' },
        { id: 'jade3d', name: '3D Jade' },
        { id: 'modernwood3d', name: '3D Modern Wood' },
        { id: 'redvblue3d', name: '3D Red/Blue' },
        { id: 'tournament3d', name: '3D Tournament' },
        { id: 'wax3d', name: '3D Wax' },
        { id: 'classicwood3d', name: '3D Classic Wood' }
    ];

    const boardThemesList = Object.keys(BOARD_THEMES).map(key => ({
        id: key,
        name: key.charAt(0).toUpperCase() + key.slice(1)
    }));

    const renderClayIcon = (id, isActive) => {
        let iconImg = null;
        if (id === 'dashboard') {
            iconImg = IconDashboard;
        } else if (id === 'chess-worlds') {
            iconImg = IconArcade;
        } else if (id === 'arena') {
            iconImg = IconArena;
        } else if (id === 'classroom') {
            iconImg = IconClassroom;
        } else if (id === 'homework') {
            iconImg = IconHomework;
        } else if (id === 'board-tools') {
            iconImg = IconTools;
        } else if (id === 'library') {
            iconImg = IconLibrary;
        }

        return (
            <div style={{
                width: '32px',
                height: '32px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                flexShrink: 0,
                transform: isActive ? 'scale(1.15) translateY(-1.5px)' : 'scale(1)'
            }}>
                {iconImg && (
                    <img 
                        src={iconImg} 
                        alt={id} 
                        style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain',
                            filter: isActive ? 'brightness(1.05) contrast(1.1)' : 'none'
                        }} 
                    />
                )}
            </div>
        );
    };

    return (
        <div style={{ 
            display: 'flex', 
            flexDirection: (isMobile || isTablet) ? 'column' : 'row',
            height: (isMobile || isTablet) ? 'auto' : '100vh', 
            minHeight: '100vh',
            overflow: (isMobile || isTablet) ? 'visible' : 'hidden', 
            backgroundColor: 'var(--bg-primary)', 
            color: 'var(--text-primary)', 
            fontFamily: "'Outfit', sans-serif" 
        }}>
            
            {/* ================= SIDEBAR / TOP BAR ================= */}
            {(isMobile || isTablet) ? (
                <div style={{ 
                    width: '100%', 
                    height: '65px',
                    backgroundColor: 'var(--bg-primary)', 
                    boxShadow: '0 4px 12px rgba(163, 177, 198, 0.25), 0 -4px 12px rgba(255, 255, 255, 0.5)', 
                    display: 'flex', 
                    flexDirection: 'row', 
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    zIndex: 10, 
                    padding: '0 15px',
                    flexShrink: 0
                }}>
                    {/* 🌟 LOGO SECTION 🌟 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img 
                            src={AcademyLogo} 
                            alt="Logo" 
                            style={{ 
                                width: '32px', 
                                height: '32px', 
                                borderRadius: '8px', 
                                objectFit: 'contain', 
                                background: 'white', 
                                boxShadow: 'var(--neu-shadow-inset)' 
                            }} 
                        />
                        {window.innerWidth > 400 && (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '13px', fontWeight: '800', letterSpacing: '0.2px', lineHeight: 1.1 }}>Hayaa Chess</h2>
                                <span style={{ color: '#2563eb', fontSize: '8px', fontWeight: '950', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Academy</span>
                            </div>
                        )}
                    </div>

                    {/* 🧭 NAVIGATION MENU */}
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'row', 
                        gap: '8px', 
                        overflowX: 'auto', 
                        flex: 1, 
                        padding: '0 10px', 
                        scrollbarWidth: 'none', 
                        msOverflowStyle: 'none' 
                    }}>
                        <style>{`
                            .mobile-nav-scroll::-webkit-scrollbar { display: none; }
                        `}</style>
                        <div className="mobile-nav-scroll" style={{ display: 'flex', flexDirection: 'row', gap: '8px', overflowX: 'auto', flex: 1, scrollbarWidth: 'none' }}>
                            {menuItems.map(item => {
                                const isActive = activeTab === item.id;
                                const iconEmoji = item.label.split(' ')[0];
                                const labelText = item.label.substring(iconEmoji.length).trim();
                                return (
                                    <div 
                                        key={item.id}
                                        onClick={() => setActiveTab(item.id)}
                                        style={{ 
                                            color: isActive ? '#2563eb' : 'var(--text-secondary)', 
                                            background: 'var(--bg-primary)',
                                            boxShadow: isActive ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                            padding: '6px 14px 6px 8px', 
                                            borderRadius: '20px', 
                                            fontSize: '13px', 
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease',
                                            fontWeight: isActive ? '700' : '500',
                                            whiteSpace: 'nowrap',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }} 
                                    >
                                        {renderClayIcon(item.id, isActive)}
                                        <span>{labelText}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* ACTIONS */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button 
                            onClick={() => setShowCustomizer(true)}
                            style={{ 
                                padding: '8px', 
                                background: 'var(--bg-primary)',
                                color: '#2563eb',
                                border: 'none',
                                borderRadius: '50%', 
                                width: '32px',
                                height: '32px',
                                display: 'flex', 
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px', 
                                cursor: 'pointer',
                                boxShadow: 'var(--neu-shadow)',
                                transition: 'all 0.2s ease',
                                outline: 'none'
                            }} 
                            title="Customize Board"
                        >
                            🎨
                        </button>
                        <button 
                            onClick={handleLogout} 
                            style={{ 
                                padding: '8px', 
                                background: 'var(--bg-primary)', 
                                color: '#ef4444', 
                                border: 'none', 
                                borderRadius: '50%', 
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                boxShadow: 'var(--neu-shadow)',
                                cursor: 'pointer', 
                                transition: 'all 0.2s ease',
                                outline: 'none'
                            }} 
                            title="Logout"
                        >
                            🚪
                        </button>
                    </div>
                </div>
            ) : (
                <div style={{ 
                    width: isSidebarCollapsed ? '80px' : '260px', 
                    height: '100vh',
                    backgroundColor: 'var(--bg-primary)', 
                    boxShadow: '9px 0 16px rgba(163, 177, 198, 0.4), -9px 0 16px rgba(255, 255, 255, 0.5)', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    zIndex: 10, 
                    padding: '10px 0',
                    flexShrink: 0,
                    transition: 'width 0.2s ease',
                    position: 'relative'
                }}>
                    
                    {/* 🌟 LOGO SECTION 🌟 */}
                    <div style={{ padding: '20px 20px', display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', gap: '15px', position: 'relative' }}>
                        <img 
                            src={AcademyLogo} 
                            alt="Hayaa Chess Academy" 
                            style={{ 
                                width: '45px', 
                                height: '45px', 
                                borderRadius: '10px', 
                                objectFit: 'contain', 
                                background: 'white', 
                                boxShadow: 'var(--neu-shadow-inset)' 
                            }} 
                        />
                        {!isSidebarCollapsed && (
                            <div>
                                <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '18px', fontWeight: '800', letterSpacing: '0.5px' }}>Hayaa Chess</h2>
                                <span style={{ color: '#2563eb', fontSize: '10px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '1px' }}>Academy</span>
                            </div>
                        )}
                        
                        {!isMobile && (
                            <button
                                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                                style={{
                                    position: 'absolute',
                                    right: '-12px',
                                    top: '25px',
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    background: 'var(--bg-primary)',
                                    boxShadow: 'var(--neu-shadow)',
                                    border: '1px solid rgba(163, 177, 198, 0.4)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    zIndex: 100,
                                    color: 'var(--text-secondary)',
                                    fontSize: '11px',
                                    outline: 'none'
                                }}
                                title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                            >
                                {isSidebarCollapsed ? '▶' : '◀'}
                            </button>
                        )}
                    </div>

                    <div style={{ padding: isSidebarCollapsed ? '15px 10px' : '15px 15px', display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', flex: 1 }}>
                        {menuItems.map(item => {
                            const isActive = activeTab === item.id;
                            const icon = item.label.split(' ')[0];
                            const labelText = item.label.substring(icon.length).trim();
                            return (
                                <div 
                                    key={item.id}
                                    onClick={() => setActiveTab(item.id)}
                                    title={isSidebarCollapsed ? labelText : ""}
                                    style={{ 
                                        color: isActive ? '#2563eb' : 'var(--text-secondary)', 
                                        background: 'var(--bg-primary)',
                                        boxShadow: isActive ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                        padding: isSidebarCollapsed ? '8px 0' : '8px 12px', 
                                        borderRadius: '12px', 
                                        display: 'flex', 
                                        alignItems: 'center',
                                        justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                                        gap: isSidebarCollapsed ? '0px' : '12px', 
                                        fontSize: '15px', 
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease-in-out',
                                        fontWeight: isActive ? '700' : '500'
                                    }} 
                                    onMouseOver={e => { 
                                        if(!isActive) {
                                            e.currentTarget.style.boxShadow = 'var(--neu-shadow-hover)';
                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                        }
                                    }} 
                                    onMouseOut={e => { 
                                        if(!isActive) {
                                            e.currentTarget.style.boxShadow = 'var(--neu-shadow)';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                        }
                                    }}
                                >
                                    {renderClayIcon(item.id, isActive)}
                                    {!isSidebarCollapsed && <span>{labelText}</span>}
                                </div>
                            );
                        })}
                    </div>

                    {/* 🎨 BOARD CUSTOMIZER TRIGGER */}
                    <button 
                        onClick={() => setShowCustomizer(true)}
                        style={{ 
                            margin: isSidebarCollapsed ? '5px auto 15px auto' : '5px 20px 15px 20px',
                            padding: isSidebarCollapsed ? '12px' : '12px 18px', 
                            background: 'var(--bg-primary)',
                            color: '#2563eb',
                            border: 'none',
                            borderRadius: '12px', 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: isSidebarCollapsed ? '0px' : '10px', 
                            fontSize: '14px', 
                            cursor: 'pointer',
                            boxShadow: 'var(--neu-shadow)',
                            transition: 'all 0.2s ease-in-out',
                            fontWeight: '700',
                            fontFamily: "'Outfit', sans-serif",
                            width: isSidebarCollapsed ? '44px' : 'auto',
                            height: isSidebarCollapsed ? '44px' : 'auto'
                        }} 
                        title={isSidebarCollapsed ? "Customize Board" : ""}
                        onMouseOver={e => { 
                            e.currentTarget.style.boxShadow = 'var(--neu-shadow-hover)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                        }} 
                        onMouseOut={e => { 
                            e.currentTarget.style.boxShadow = 'var(--neu-shadow)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        <span>🎨</span>
                        {!isSidebarCollapsed && <span>Customize Board</span>}
                    </button>

                    {/* 👤 USER PROFILE BADGE */}
                    <div style={{ 
                        margin: isSidebarCollapsed ? '10px auto' : '10px 20px 15px 20px',
                        padding: isSidebarCollapsed ? '12px 6px' : '12px 15px',
                        borderRadius: '16px',
                        backgroundColor: 'var(--bg-primary)',
                        boxShadow: 'var(--neu-shadow-inset)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                        gap: isSidebarCollapsed ? '0px' : '10px',
                        border: '1px solid rgba(163, 177, 198, 0.3)',
                        flexShrink: 0,
                        width: isSidebarCollapsed ? '44px' : 'auto'
                    }} title={isSidebarCollapsed ? (user?.email ? user.email.split('@')[0] : 'User') : ""}>
                        <CartoonAvatar
                            name={userName || user?.email?.split('@')[0] || (role === 'OWNER' ? 'Owner' : role === 'COACH' ? 'Coach' : 'Student')}
                            size={36}
                            avatarUrl={avatar}
                        />
                        {!isSidebarCollapsed && (
                            <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                <span style={{ fontSize: '9px', fontWeight: '800', textTransform: 'uppercase', color: '#2563eb', letterSpacing: '0.5px', lineHeight: '1.2' }}>
                                    {role === 'OWNER' ? 'Owner' : role === 'COACH' || role === 'CHIEF_COACH' ? 'Coach' : 'Student'}
                                </span>
                                <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.2' }} title={user?.email}>
                                    {user?.email ? user.email.split('@')[0] : 'User'}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* 🚪 LOGOUT BUTTON */}
                    <div style={{ padding: isSidebarCollapsed ? '0 auto 20px auto' : '0 20px 20px 20px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                        <button 
                            onClick={handleLogout} 
                            style={{ 
                                width: isSidebarCollapsed ? '44px' : '100%', 
                                height: isSidebarCollapsed ? '44px' : 'auto',
                                padding: isSidebarCollapsed ? '10px' : '12px', 
                                background: 'var(--bg-primary)', 
                                color: '#ef4444', 
                                border: 'none', 
                                borderRadius: '12px', 
                                boxShadow: 'var(--neu-shadow)',
                                cursor: 'pointer', 
                                fontWeight: 'bold', 
                                fontSize: '15px', 
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px'
                            }} 
                            title={isSidebarCollapsed ? "Logout" : ""}
                            onMouseOver={e => { 
                                e.currentTarget.style.boxShadow = 'var(--neu-shadow-hover)';
                                e.currentTarget.style.background = '#fee2e2';
                            }} 
                            onMouseOut={e => { 
                                e.currentTarget.style.boxShadow = 'var(--neu-shadow)';
                                e.currentTarget.style.background = 'var(--bg-primary)';
                            }}
                            onMouseDown={e => {
                                e.currentTarget.style.boxShadow = 'var(--neu-shadow-inset)';
                            }}
                        >
                            <span>🚪</span>
                            {!isSidebarCollapsed && <span>Logout</span>}
                        </button>
                    </div>
                </div>
            )}

            {/* ================= MAIN CONTENT AREA ================= */}
            <div style={{ 
                flex: 1, 
                height: (isMobile || isTablet) ? 'auto' : '100vh', 
                overflowY: (isMobile || isTablet) ? 'visible' : 'auto', 
                display: 'flex', 
                flexDirection: 'column' 
            }}>
                {/* Pages render directly inside the padded content viewport */}
                <div style={{ padding: (isClassroomLive || (role === 'STUDENT' && joinedLiveClass)) ? '0px' : '20px', flex: 1, display: 'flex', flexDirection: 'column', height: (isMobile || isTablet) ? 'auto' : '100%', minHeight: 0 }}>
                    {children} 
                </div>
            </div>

            {/* ================= THEME CUSTOMIZER DRAWER MODAL ================= */}
            {showCustomizer && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(0, 0, 0, 0.4)',
                    backdropFilter: 'blur(8px)',
                    zIndex: 99999,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '20px'
                }}>
                    <div className="glass-panel" style={{
                        width: '100%',
                        maxWidth: '650px',
                        maxHeight: '85vh',
                        background: 'var(--bg-primary)',
                        borderRadius: '24px',
                        border: '2px solid var(--border-glass)',
                        boxShadow: 'var(--panel-shadow)',
                        padding: '30px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        overflow: 'hidden',
                        color: 'var(--text-primary)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(163, 177, 198, 0.4)', paddingBottom: '15px' }}>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#2563eb' }}>🎨 Board Customizer</h2>
                            <button onClick={() => setShowCustomizer(false)} style={{ background: 'transparent', border: 'none', color: '#ef4444', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer' }}>✖</button>
                        </div>
                        
                        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '25px', paddingRight: '5px' }}>
                            {/* Board Themes Section */}
                            <div>
                                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '700' }}>Select Board Style</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px' }}>
                                    {boardThemesList.map(t => {
                                        const colors = BOARD_THEMES[t.id];
                                        const isSel = boardTheme === t.id;
                                        return (
                                            <div key={t.id} onClick={() => handleSelectTheme(t.id, pieceTheme)} style={{
                                                padding: '10px',
                                                borderRadius: '12px',
                                                background: 'var(--bg-primary)',
                                                boxShadow: isSel ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                                border: isSel ? '2px solid #2563eb' : '2px solid transparent',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                transition: 'all 0.2s'
                                            }}>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', width: '28px', height: '28px', borderRadius: '4px', overflow: 'hidden', flexShrink: 0 }}>
                                                    <div style={{ backgroundColor: colors.light }} />
                                                    <div style={{ backgroundColor: colors.dark }} />
                                                    <div style={{ backgroundColor: colors.dark }} />
                                                    <div style={{ backgroundColor: colors.light }} />
                                                </div>
                                                <span style={{ fontSize: '13px', fontWeight: '600' }}>{t.name}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Piece Themes Section */}
                            <div>
                                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '700' }}>Select Piece Theme</h3>
                                
                                {/* 3D Pieces Sub-section */}
                                <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#f59e0b', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>✨ 3D Chess Pieces</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                                    {pieceThemes3D.map(t => {
                                        const isSel = pieceTheme === t.id;
                                        const knightImg = PIECE_SETS[t.id]?.wN;
                                        return (
                                            <div key={t.id} onClick={() => handleSelectTheme(boardTheme, t.id)} style={{
                                                padding: '10px',
                                                borderRadius: '12px',
                                                background: 'var(--bg-primary)',
                                                boxShadow: isSel ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                                border: isSel ? '2px solid #2563eb' : '2px solid transparent',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '8px',
                                                transition: 'all 0.2s',
                                                textAlign: 'center'
                                            }}>
                                                {knightImg && <img src={knightImg} alt={t.name} style={{ width: '40px', height: '40px', objectFit: 'contain' }} />}
                                                <span style={{ fontSize: '11px', fontWeight: '700' }}>{t.name}</span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* 2D Pieces Sub-section */}
                                <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#3b82f6', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>♟️ 2D Classic Pieces</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px' }}>
                                    {pieceThemes2D.map(t => {
                                        const isSel = pieceTheme === t.id;
                                        const knightImg = PIECE_SETS[t.id]?.wN;
                                        return (
                                            <div key={t.id} onClick={() => handleSelectTheme(boardTheme, t.id)} style={{
                                                padding: '10px',
                                                borderRadius: '12px',
                                                background: 'var(--bg-primary)',
                                                boxShadow: isSel ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                                border: isSel ? '2px solid #2563eb' : '2px solid transparent',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                transition: 'all 0.2s'
                                            }}>
                                                {knightImg && <img src={knightImg} alt={t.name} style={{ width: '28px', height: '28px', objectFit: 'contain', flexShrink: 0 }} />}
                                                <span style={{ fontSize: '12px', fontWeight: '600' }}>{t.name}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid rgba(163, 177, 198, 0.4)', paddingTop: '15px' }}>
                            <button onClick={() => setShowCustomizer(false)} className="neu-btn-primary" style={{ padding: '10px 24px', fontSize: '14px' }}>Done</button>
                        </div>
                    </div>
                </div>
            )}
            
        </div>
    );
}
